/**
 * ProgressOverlay (Phase 23.2.3 — Living SOPs)
 *
 * Full-screen translucent overlay that shows cascade progress for the
 * approve-then-publish flow. Mounted by ProposalDetailPage after the approve
 * mutation resolves; reads cascade status from useCascadeStatus (500 ms poll).
 *
 * States:
 *  - 'cascading' — progress bar + step N/total + elapsed time
 *  - 'complete'  — green check + "v1.02 published" + Close button (auto-close)
 *  - 'timeout'   — client-side timer exceeded APPROVE_CLIENT_TIMEOUT_SECONDS;
 *                  switches to an amber "Still working" message. Polling keeps
 *                  running in the background; the resume worker is the
 *                  backstop if the cascade actually stalled.
 *
 * Accessibility:
 *  - role="dialog" + aria-modal="true"
 *  - aria-live="polite" on the progress region so screen readers announce
 *    stage transitions ("step 3 of 6, Regenerating questions").
 *  - Escape key is NOT wired to close during an active cascade (prevents
 *    accidental navigation); it IS wired on 'complete' / 'timeout' states.
 *  - Focus-trap is kept intentionally minimal — the overlay contains at most
 *    one interactive element (a Close button on terminal states).
 *
 * The overlay does NOT dismiss itself on 'cascading'; ProposalDetailPage
 * controls the open/close state via a parent `isOpen` prop.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import type { CascadeStatus } from '../../../hooks/useSopProposals';

// Client-side timeout before the overlay flips to 'timeout' state.
//
// Mirrors LIVING_SOPS_CONFIG.INBOX.APPROVE_CLIENT_TIMEOUT_SECONDS on the
// backend (60s). Hard-coded here because @guild/config is a Node-only package
// (references `process.env`) and cannot be imported into the web build. If the
// server-side threshold moves, update this constant to stay in sync.
const CLIENT_TIMEOUT_MS = 60 * 1000;

interface ProgressOverlayProps {
  /** Controls mount/unmount. Parent should only render when a cascade is active. */
  isOpen: boolean;
  /** Latest status from useCascadeStatus; undefined during initial poll. */
  status: CascadeStatus | undefined;
  /**
   * Fires once when status transitions to 'complete'. Parent typically
   * navigates to the SOP detail page and dismisses the overlay.
   */
  onComplete?: (result: {
    versionId: string;
    versionNumber: string;
    changeSummary: string | null;
  }) => void;
  /**
   * Fires once when the client-side timeout elapses without the cascade
   * reaching 'complete'. Parent typically logs a warning toast and keeps
   * the overlay open in 'timeout' state until the user clicks Close.
   */
  onTimeout?: () => void;
  /** Close button handler; only invoked from 'complete' and 'timeout' states. */
  onClose?: () => void;
}

export function ProgressOverlay({
  isOpen,
  status,
  onComplete,
  onTimeout,
  onClose,
}: ProgressOverlayProps) {
  // Client-side elapsed timer drives the timeout state. We track wall-clock
  // time since the overlay opened (NOT since the cascade started on the
  // server — those clocks can drift, and what we care about is "has the
  // trainer waited too long").
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);
  const [completedNotified, setCompletedNotified] = useState(false);
  const [timeoutNotified, setTimeoutNotified] = useState(false);

  // Reset timing state each time the overlay opens.
  useEffect(() => {
    if (isOpen) {
      setOpenedAt(Date.now());
      setNow(Date.now());
      setTimedOut(false);
      setCompletedNotified(false);
      setTimeoutNotified(false);
    }
  }, [isOpen]);

  // Tick the "now" clock every 500 ms while open so the elapsed counter + the
  // timeout check stay live. 500 ms matches the server poll cadence.
  useEffect(() => {
    if (!isOpen) return;
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(tick);
  }, [isOpen]);

  // Timeout detection — flip to 'timeout' state once the elapsed exceeds the
  // config threshold AND the cascade hasn't completed. We do NOT cancel the
  // query; polling continues in the background so if the cascade finishes a
  // minute later the overlay can transition into 'complete' still.
  useEffect(() => {
    if (!isOpen || !openedAt) return;
    if (status?.stage === 'complete') return;
    const elapsed = now - openedAt;
    if (elapsed >= CLIENT_TIMEOUT_MS && !timedOut) {
      setTimedOut(true);
    }
  }, [isOpen, openedAt, now, status, timedOut]);

  // Fire onComplete / onTimeout exactly once each.
  useEffect(() => {
    if (!isOpen) return;
    if (status?.stage === 'complete' && !completedNotified) {
      setCompletedNotified(true);
      onComplete?.({
        versionId: status.versionId,
        versionNumber: status.versionNumber,
        changeSummary: status.changeSummary,
      });
    }
  }, [isOpen, status, completedNotified, onComplete]);

  useEffect(() => {
    if (!isOpen) return;
    if (timedOut && !timeoutNotified) {
      setTimeoutNotified(true);
      onTimeout?.();
    }
  }, [isOpen, timedOut, timeoutNotified, onTimeout]);

  // Escape key closes ONLY on terminal states (complete or timeout).
  useEffect(() => {
    if (!isOpen) return;
    const isTerminal = status?.stage === 'complete' || timedOut;
    if (!isTerminal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, status, timedOut, onClose]);

  if (!isOpen) return null;

  // Derive display state.
  const isComplete = status?.stage === 'complete';
  const isCascading = !isComplete && !timedOut;
  const elapsedMs = openedAt ? Math.max(0, now - openedAt) : 0;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  const elapsedLabel = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  // Step / total derivations for both 'cascading' and 'complete' states.
  const total = status?.total ?? 6;
  const step =
    status && status.stage !== 'idle' ? status.step : isCascading ? 1 : 0;
  const progressPct = total > 0 ? Math.min(100, (step / total) * 100) : 0;
  const label =
    status && status.stage !== 'idle' ? status.label : 'Starting publish…';

  // Version number only exists in 'complete' status.
  const versionNumber =
    status?.stage === 'complete' ? status.versionNumber : null;
  const changeSummary =
    status?.stage === 'complete' ? status.changeSummary : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cascade-overlay-title"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cascading state */}
        {isCascading && (
          <>
            <div className="flex items-start gap-3">
              <Loader2
                size={24}
                className="mt-1 flex-shrink-0 animate-spin text-guild-green"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <h2
                  id="cascade-overlay-title"
                  className="text-lg font-bold text-gray-900"
                >
                  Publishing changes
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Regenerating questions, embeddings, and flagging videos.
                </p>
              </div>
            </div>

            <div
              className="mt-6 space-y-3"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {/* Progress bar */}
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-gray-100"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={step}
                aria-valuetext={`Step ${step} of ${total}: ${label}`}
              >
                <div
                  className="h-full bg-guild-green transition-[width] duration-300 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              {/* Step label + elapsed */}
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-gray-800">
                  Step {step} of {total}: {label}
                </span>
                <span className="tabular-nums text-gray-500">
                  {elapsedLabel}
                </span>
              </div>
            </div>

            <p className="mt-5 text-xs text-gray-500">
              Do not close this tab. The cascade usually takes 8 to 30 seconds.
            </p>
          </>
        )}

        {/* Complete state */}
        {isComplete && (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 rounded-full bg-emerald-50 p-2">
                <Check
                  size={20}
                  className="text-emerald-600"
                  strokeWidth={3}
                  aria-hidden="true"
                />
              </div>
              <div className="min-w-0">
                <h2
                  id="cascade-overlay-title"
                  className="text-lg font-bold text-gray-900"
                >
                  {versionNumber
                    ? `v${versionNumber} published`
                    : 'Version published'}
                </h2>
                <p
                  className="mt-1 text-sm text-gray-600"
                  role="status"
                  aria-live="polite"
                >
                  The SOP is now live. Operators will see the updated content
                  on their next refresh.
                </p>
              </div>
            </div>

            {changeSummary && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                <p className="font-semibold text-xs uppercase tracking-wide text-gray-500">
                  What changed
                </p>
                <p className="mt-1 whitespace-pre-wrap">{changeSummary}</p>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
              <span className="tabular-nums">Completed in {elapsedLabel}</span>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-guild-green px-4 py-2 text-sm font-semibold text-white hover:bg-guild-green/90"
              >
                Close
              </button>
            </div>
          </>
        )}

        {/* Timeout state (client-side threshold exceeded) */}
        {!isComplete && timedOut && (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 rounded-full bg-amber-50 p-2">
                <AlertCircle
                  size={20}
                  className="text-amber-600"
                  aria-hidden="true"
                />
              </div>
              <div className="min-w-0">
                <h2
                  id="cascade-overlay-title"
                  className="text-lg font-bold text-gray-900"
                >
                  Still working…
                </h2>
                <p
                  className="mt-1 text-sm text-gray-600"
                  role="status"
                  aria-live="polite"
                >
                  The cascade is taking longer than expected. Your changes are
                  still being applied in the background — check Notifications
                  for the completion update, or return to this page later.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
              <span className="tabular-nums">Elapsed {elapsedLabel}</span>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
